import { useEffect, useRef, useState } from 'react';
import { useApiClient } from '@api-client/context';

interface Colleague {
  id: number;
  full_name: string;
  email?: string;
}

interface TransferCallControlProps {
  onTransfer: (userId: number) => void;
  isTransferring: boolean;
  /** Excluded from the list: you cannot transfer a call to yourself. */
  currentUserId?: number;
}

/**
 * Hands the live call to a colleague.
 *
 * Rendered beside the active call widget rather than inside it, because the shared
 * CallNotification component has no slot for extra controls.
 */
export default function TransferCallControl({
  onTransfer,
  isTransferring,
  currentUserId
}: TransferCallControlProps) {
  const api = useApiClient();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [colleagues, setColleagues] = useState<Colleague[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    searchInputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);

    // Debounced so typing does not fire a request per keystroke while a caller is waiting.
    const timer = setTimeout(() => {
      api.team
        .getTeamMemberBySearchList({ page: 1, perPage: 20 }, searchTerm || undefined)
        .then((response) => {
          if (cancelled) {
            return;
          }
          const items = (response?.items ?? []) as Colleague[];
          setColleagues(items.filter((colleague) => colleague.id !== currentUserId));
        })
        .catch(() => {
          if (!cancelled) {
            setLoadError('Could not load colleagues.');
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoading(false);
          }
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [api, isOpen, searchTerm, currentUserId]);

  if (!isOpen) {
    return (
      <button
        type="button"
        className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        onClick={() => setIsOpen(true)}
        disabled={isTransferring}
        data-testid="transfer-call-open"
      >
        {isTransferring ? 'Transferring…' : 'Transfer'}
      </button>
    );
  }

  return (
    <div
      className="mt-1 w-full rounded border border-gray-300 bg-white p-2 shadow"
      data-testid="transfer-call-panel"
    >
      <input
        ref={searchInputRef}
        type="text"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        placeholder="Search colleagues"
        aria-label="Search colleagues"
        className="mb-2 w-full rounded border border-gray-300 px-2 py-1 text-sm"
        data-testid="transfer-call-search"
      />

      {isLoading ? <p className="px-1 py-2 text-sm text-gray-500">Loading…</p> : null}
      {loadError ? <p className="px-1 py-2 text-sm text-red-600">{loadError}</p> : null}
      {!isLoading && !loadError && colleagues.length === 0 ? (
        <p className="px-1 py-2 text-sm text-gray-500">No colleagues found.</p>
      ) : null}

      <ul className="max-h-40 overflow-y-auto">
        {colleagues.map((colleague) => (
          <li key={colleague.id}>
            <button
              type="button"
              className="w-full truncate rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 disabled:opacity-50"
              onClick={() => {
                setIsOpen(false);
                onTransfer(colleague.id);
              }}
              disabled={isTransferring}
              data-testid={`transfer-call-to-${colleague.id}`}
            >
              {colleague.full_name}
            </button>
          </li>
        ))}
      </ul>

      <button
        type="button"
        className="mt-1 w-full rounded px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
        onClick={() => setIsOpen(false)}
        data-testid="transfer-call-cancel"
      >
        Cancel
      </button>
    </div>
  );
}
